'use client';

import React from 'react';
import { Alert, Box, HStack, IconButton } from '@axon/ui';
import { LuX, LuInfo, LuCircleCheck, LuCircleAlert, LuTriangleAlert } from 'react-icons/lu';

import type { AlertBannerOptions } from '@/lib/notification-manager';

interface AlertBannerProps extends AlertBannerOptions {
  onDismiss: () => void;
}

const statusIcons = {
  success: LuCircleCheck,
  error: LuCircleAlert,
  warning: LuTriangleAlert,
  info: LuInfo,
};

const statusColors = {
  success: 'green',
  error: 'red',
  warning: 'orange',
  info: 'blue',
};

export function AlertBanner({
  title,
  description,
  type,
  dismissible = true,
  action,
  onDismiss,
}: AlertBannerProps) {
  const Icon = statusIcons[type];
  const _colorPalette = statusColors[type];

  return (
    <Alert.Root status={type} variant="top-accent">
      <Alert.Indicator>
        <Icon />
      </Alert.Indicator>
      <Box flex="1">
        <Alert.Title>{title}</Alert.Title>
        {description && <Alert.Description>{description}</Alert.Description>}
      </Box>
      <HStack gap={2}>
        {action && (
          <button
            onClick={action.onClick}
            className={`px-3 py-1 text-sm font-medium rounded-md hover:opacity-80 transition-opacity ${
              type === 'error'
                ? 'bg-red-600 text-white'
                : type === 'warning'
                  ? 'bg-orange-600 text-white'
                  : type === 'success'
                    ? 'bg-green-600 text-white'
                    : 'bg-blue-600 text-white'
            }`}
          >
            {action.label}
          </button>
        )}
        {dismissible && (
          <IconButton
            aria-label="Dismiss"
            size="sm"
            variant="ghost"
            icon={<LuX />}
            onClick={onDismiss}
          />
        )}
      </HStack>
    </Alert.Root>
  );
}
